// Shared operator vocabulary for conditional-logic rules (RuleBuilder.tsx) and
// pattern-mode option sourcing (OptionSourceEditor.tsx).
//
// The same set is also evaluated at runtime on mobile, which has its own
// evaluateOperator() in apps/mobile/src/utils/questionVisibility.ts. Keep that
// implementation's operator handling in lockstep; the operator list here is the
// source of truth.

import type { QuestionType } from './RuleBuilder';

export type Operator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'is_empty'
  | 'is_not_empty';

export const OPERATOR_LABELS: Record<Operator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  gt: 'greater than',
  gte: 'greater or equal',
  lt: 'less than',
  lte: 'less or equal',
  between: 'is between',
  is_empty: 'is empty',
  is_not_empty: 'is answered',
};

export function operatorsForType(type: QuestionType | undefined): Operator[] {
  if (!type) return ['equals', 'not_equals'];
  if (type === 'multiple_choice') return ['contains', 'not_contains', 'is_empty', 'is_not_empty'];
  if (type === 'single_choice' || type === 'dropdown') {
    return ['equals', 'not_equals', 'is_empty', 'is_not_empty'];
  }
  if (type === 'text') {
    return ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'];
  }
  if (type === 'number') {
    return ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'];
  }
  if (type === 'date' || type === 'photo' || type === 'file') return ['is_empty', 'is_not_empty'];
  return ['equals', 'not_equals'];
}

export function needsValue(op: Operator): boolean {
  return op !== 'is_empty' && op !== 'is_not_empty';
}

export function needsRangeValue(op: Operator): boolean {
  return op === 'between';
}
